import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { Drawer, DrawerProps } from '../Drawer';

export interface SplitLayoutDrawerProps extends Omit<DrawerProps, 'children'> {
  mainContent: React.ReactNode;
  sidebarContent: React.ReactNode;
  drawerWidth?: number | string;
  sidebarWidth?: number | string;
  buttonsRightAligned?: boolean;
}

export const DrawerSplitContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

export const DrawerMainArea = styled.div<{ $percent?: number }>`
  ${({ $percent }) => ($percent ? `flex: 0 0 ${$percent}%;` : `flex: 1;`)}
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background-color: ${theme.dime.colors.fill.primary};
`;

export const DrawerSidebar = styled.div<{ $width?: number | string }>`
  ${({ $width }) => {
    if (typeof $width === 'string' && $width.includes('%')) {
      return `flex: 0 0 ${$width};`;
    }
    if (typeof $width === 'number') {
      return `width: ${$width}px; flex-shrink: 0;`;
    }
    return '';
  }}

  display: flex;
  flex-direction: column;
  border-left: 1px solid ${theme.dime.colors.border.static};
  background-color: ${theme.dime.colors.fill.primary};
`;

export const DrawerSidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

export const SplitLayoutDrawer: React.FC<SplitLayoutDrawerProps> = ({
  open,
  title,
  mainContent,
  sidebarContent,
  onClose,
  onSubmit,
  onCancel,
  submitText = 'Submit',
  cancelText = 'Cancel',
  submitDisabled = false,
  submitLoading = false,
  cancelDisabled = false,
  className,
  destroyOnClose,
  drawerWidth = 1566,
  sidebarWidth = 520,
  buttonsRightAligned = true,
  hideFooter,
  width,
  placement = 'right',
  analytics
}) => {
  const isSidebarPercent =
    typeof sidebarWidth === 'string' && sidebarWidth.includes('%');
  const mainContentPercent = isSidebarPercent
    ? 100 - parseFloat(sidebarWidth)
    : undefined;
  return (
    <Drawer
      analytics={analytics}
      open={open}
      title={title}
      onClose={onClose}
      onSubmit={onSubmit}
      onCancel={onCancel}
      submitText={submitText}
      cancelText={cancelText}
      submitDisabled={submitDisabled}
      submitLoading={submitLoading}
      cancelDisabled={cancelDisabled}
      width={drawerWidth ?? width}
      className={className}
      placement={placement}
      destroyOnClose={destroyOnClose}
      layout="split"
      buttonsRightAligned={buttonsRightAligned}
      hideFooter={hideFooter}
    >
      <DrawerSplitContent>
        <DrawerMainArea $percent={mainContentPercent}>
          {mainContent}
        </DrawerMainArea>
        <DrawerSidebar $width={sidebarWidth}>
          <DrawerSidebarContent>{sidebarContent}</DrawerSidebarContent>
        </DrawerSidebar>
      </DrawerSplitContent>
    </Drawer>
  );
};
